import type { EntityData, PublishingOptionType } from '@ContractBuilder/types';
import type { BlockError, RootResourceStatus } from '@root/@types/types';
import { ContractError, DocumentMode, TEMPLATE_REVIEW_STATUSES, UserRole } from '@root/@types/types';
import { getAllBlocks } from '@root/helpers/blocks';
import {
  ENDORSEMENT_TRANSITION_STATUSES,
  getAllowedEndorsementTargetStatuses,
} from '@root/helpers/endorsements/state-transitions';
import { isNonNullish } from '@root/helpers/isNonNullish';
import { getAllowedTemplateTargetStatuses } from '@root/helpers/templates/state-transitions';
import { get } from 'lodash-es';

export const getBlockValidationErrors = (isSubmission: boolean, submission?: EntityData) => {
  if (isSubmission) {
    const allBlocks = getAllBlocks(submission);
    const blockErrors = allBlocks.flatMap((block) => block.validationErrors);

    return [...new Set(blockErrors)].filter(isNonNullish);
  }

  return [];
};

export const getContractValidationErrors = (isSubmission: boolean, isValid: boolean, submission?: EntityData) => {
  if (isSubmission) {
    const result: { type: ContractError; error: Record<string, string>[] }[] = [];
    if (!isValid) {
      result.push({
        type: ContractError.InvalidForm,
        error: [], // TODO: add errors
      });
    }

    const hasMissingDataPoints = !!submission?.contract_missing_data?.length;
    if (hasMissingDataPoints) {
      result.push({ type: ContractError.MissingDatapoints, error: submission?.contract_missing_data ?? [] });
    }

    if (submission?.hasMaterialChanges === false) {
      result.push({ type: ContractError.NoMaterialChanges, error: [] });
    }

    return result;
  }

  return [];
};

export const isDocumentValid = (blockErrors: BlockError[], contractErrors: { type: ContractError }[]): boolean =>
  blockErrors.length === 0 && !contractErrors.some(({ type }) => type === ContractError.InvalidForm);

export const isCurrentStatus = (currentStatus?: string, status?: string) => status && currentStatus === status;
export const isFirmOrderStatus = (id: string) => id === 'FIRM_ORDER';
export const isDraftStatus = (status?: string) => status === 'DRAFT';
export const isReviewStatus = (status?: string) => status === 'REVIEW';

type AllowedStatusesMap = Partial<Record<RootResourceStatus, RootResourceStatus[]>>;

const getSubmissionAllowedStatuses = (isFirmOrderEnabled?: boolean): AllowedStatusesMap => ({
  ARCHIVED: ['ARCHIVED', 'DRAFT'],
  DRAFT: ['ARCHIVED', 'DRAFT', 'REVIEW', 'PUBLISHED'],
  REVIEW: ['ARCHIVED', 'DRAFT', 'REVIEW', isFirmOrderEnabled ? 'FIRM_ORDER' : 'FINAL'],
  FINAL: ['DRAFT', 'REVIEW', 'FIRM_ORDER', 'FINAL'],
  FIRM_ORDER: ['DRAFT', 'REVIEW', 'ARCHIVED', 'FIRM_ORDER', 'FINAL'],
});

const DEFAULT_ALLOWED_STATUSES: AllowedStatusesMap = {
  ARCHIVED: ['ARCHIVED', 'DRAFT'],
  DRAFT: ['ARCHIVED', 'DRAFT', 'FINAL', 'PUBLISHED'],
  REVIEW: ['ARCHIVED', 'DRAFT', 'REVIEW', 'FINAL'],
  FINAL: ['DRAFT', 'REVIEW', 'FIRM_ORDER', 'FINAL'],
  FIRM_ORDER: ['DRAFT', 'REVIEW', 'ARCHIVED', 'FIRM_ORDER', 'FINAL'],
};

const getAllowedStatusesMap = ({
  documentMode,
  isAdmin,
  isEndorsementReviewEnabled,
  isFirmOrderEnabled,
  isSuperadmin,
  isTemplateReviewWorkflowEnabled,
}: {
  documentMode?: DocumentMode;
  isAdmin?: boolean;
  isEndorsementReviewEnabled?: boolean;
  isFirmOrderEnabled?: boolean;
  isSuperadmin?: boolean;
  isTemplateReviewWorkflowEnabled?: boolean;
}): AllowedStatusesMap => {
  if (documentMode === DocumentMode.TEMPLATES) {
    return Object.fromEntries(
      TEMPLATE_REVIEW_STATUSES.map((status) => {
        const transitions = getAllowedTemplateTargetStatuses({
          sourceStatus: status,
          isTemplateReviewWorkflowEnabled: !!isTemplateReviewWorkflowEnabled,
          userRole: isSuperadmin ? UserRole.Superadmin : undefined,
        });
        return [status, [status, ...transitions]];
      }),
    ) as AllowedStatusesMap;
  }

  if (documentMode === DocumentMode.ENDORSEMENTS) {
    return Object.fromEntries(
      ENDORSEMENT_TRANSITION_STATUSES.map((status) => [
        status,
        [status, ...getAllowedEndorsementTargetStatuses({ sourceStatus: status, isAdmin, isEndorsementReviewEnabled })],
      ]),
    ) as AllowedStatusesMap;
  }

  if (documentMode === DocumentMode.SUBMISSIONS) {
    return getSubmissionAllowedStatuses(isFirmOrderEnabled);
  }

  return DEFAULT_ALLOWED_STATUSES;
};

const getAllowedStatusesAsAdmin = (
  publishingOptions: PublishingOptionType[],
  currentStatus?: string,
  allowedStatusesForStatus: RootResourceStatus[] = [],
) => {
  if (currentStatus === 'FINAL') {
    return publishingOptions.filter(({ id }) => id !== 'ARCHIVED');
  }

  return publishingOptions.filter(({ id }) => allowedStatusesForStatus.includes(id));
};

export const filterPublishingOptions = ({
  documentMode,
  isAdmin,
  isEndorsementReviewEnabled,
  isFirmOrderEnabled,
  isSuperadmin,
  isTemplateReviewWorkflowEnabled,
  publishingOptions,
  status,
}: {
  publishingOptions: PublishingOptionType[];
  status?: string;
  isFirmOrderEnabled?: boolean;
  isAdmin?: boolean;
  documentMode?: DocumentMode;
  isEndorsementReviewEnabled?: boolean;
  isSuperadmin?: boolean;
  isTemplateReviewWorkflowEnabled?: boolean;
}) => {
  const allowedStatuses = getAllowedStatusesMap({
    documentMode,
    isAdmin,
    isEndorsementReviewEnabled,
    isFirmOrderEnabled,
    isSuperadmin,
    isTemplateReviewWorkflowEnabled,
  });
  const allowedStatusesForStatus = get(allowedStatuses, status ?? '', []) as RootResourceStatus[];

  if (!allowedStatusesForStatus.length) {
    return publishingOptions;
  }

  if (isAdmin) {
    return getAllowedStatusesAsAdmin(publishingOptions, status, allowedStatusesForStatus);
  }

  return publishingOptions.filter(({ id }) => allowedStatusesForStatus.includes(id));
};
